/**
 * Section: Hero banner
 * ------------------------------------------------------------------------------
 * Large component displaying image galleries in a carousel.
 *
 * @namespace heroBanner
 */
import { register } from '@shopify/theme-sections';
import Carousel from '~/components/carousel';
/**
 * DOM selectors.
 */

const selectors = {
  id: 'HeroBanner',
  pagination: '[js-hero-banner="pagination"]',
  prevButton: '[js-hero-banner="prev"]',
  nextButton: '[js-hero-banner="next"]'
};
/**
 * Instance globals.
 */

let heroBanner = {};
/**
 * Image slider section constructor.
 * Executes on page load as well as Theme Editor `section:load` events.
 */

register('hero-banner', {
  /**
   * Defaults.
   */
  getConfig() {
    return {
      swiper: {
        autoplay: this.getAutoplaySetting(),
        spaceBetween: 0,
        effect: 'fade',
        navigation: {
          nextEl: this.cache.nextButton,
          prevEl: this.cache.prevButton
        }
      }
    };
  },

  /**
   * Cache selectors.
   * - Define necessary variables and make available for component use.
   */
  cacheSelectors() {
    const container = "#".concat(selectors.id, "-").concat(this.id);
    const pagination = "".concat(container, " ").concat(selectors.pagination);
    const prevButton = "".concat(container, " ").concat(selectors.prevButton);
    const nextButton = "".concat(container, " ").concat(selectors.nextButton);
    this.cache = {
      container,
      pagination,
      nextButton,
      prevButton
    };
  },

  /**
   * Set cached node selectors.
   */
  setNodeSelectors() {
    this.container = document.querySelector(this.cache.container);
    this.pagination = document.querySelector(this.cache.pagination);
  },

  /**
   * Fetches carousel autoplay setting
   * - Reads data-autoplay attribute on carousel container.
   * @returns {Boolean|Object} Autoplay setting object formatted for Swiper.
   */
  getAutoplaySetting() {
    const autoplay = Number(this.container.dataset.autoplay);

    if (!autoplay) {
      return false;
    }

    return {
      delay: autoplay
    };
  },

  /**
   * Construct carousel.
   */
  constructCarousel() {
    if (!this.container) {
      throw new Error("Could not find container of ".concat(this.cache.container));
    }

    heroBanner = Carousel(this.cache.container, this.getConfig());
    heroBanner.init();
    this.swiperInstance = heroBanner.getSwiperInstance();
  },

  /**
   * Event callback when Section is registered in scripts.
   */
  onLoad() {
    this.cacheSelectors();
    this.setNodeSelectors();
    this.constructCarousel();
  },

  /**
   * Event callback for Theme Editor `section:unload` event.
   */
  onUnload() {
    heroBanner.resize();
  },

  /**
   * Event callback for Theme Editor `section:select` event.
   */
  onSelect() {
    heroBanner.resize();
  },

  onBlockSelect(event) {
    this.swiperInstance.autoplay.stop();
    const slide = event.target;
    const slideId = slide.dataset.swiperSlideIndex;
    heroBanner.select(slideId, 0);
  },

  onBlockDeselect() {
    this.swiperInstance.autoplay.start();
  }

});